import { useEffect } from 'react';
import { ArrowRight, Calendar, MapPin, ExternalLink, CheckCircle2 } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { openCalendlyDemo } from '@/lib/analytics';

interface Conference {
  name: string;
  fullName?: string;
  dates: string;
  location: string;
  tag: string;
  blurb?: string;
  detailsUrl?: string;
  role?: string; // past events: "Exhibited" | "Attended"
}

const upcoming: Conference[] = [
  {
    name: 'APA Conference & Awards 2026',
    dates: 'October 13, 2026',
    location: 'Birmingham, UK',
    tag: 'AP — UK & Europe',
    blurb:
      "The Accounts Payable Association's flagship UK conference — a full day of AP panels and workshops plus the industry awards gala. Meet us to talk Oracle AP automation and exception prevention with UK and European finance teams.",
    detailsUrl: 'https://ap-association.com/conference-overview',
  },
  {
    name: 'Oracle AI World 2026',
    dates: 'October 25–28, 2026',
    location: 'The Venetian Convention & Expo Center · Las Vegas, NV',
    tag: 'Oracle ecosystem',
    blurb:
      "Oracle's flagship event for the Fusion, EBS, and OCI community. EZ Cloud is published on the Oracle Reference Architecture for OCI and featured in Oracle's technical case studies — come see how we automate AP operations natively inside Oracle Fusion and E-Business Suite.",
    detailsUrl: 'https://www.oracle.com/ai-world/',
  },
  {
    name: 'IOFM AP & P2P Fall Conference & Expo 2026',
    dates: 'November 2–4, 2026',
    location: 'JW Marriott Austin · Austin, TX',
    tag: 'AP & P2P',
    blurb:
      "The Institute of Finance & Management's flagship Accounts Payable & Procure-to-Pay event, where AP and shared-services leaders benchmark automation. Meet us to see AI email exception prevention and native ERP posting in action.",
    detailsUrl: 'https://events.iofm.com/conference-fall/',
  },
  {
    name: 'CGCS CFO Conference 2026',
    fullName:
      'Chief Financial Officer & Directors of Procurement, Risk Management, and Internal Audit Conference',
    dates: 'November 17–20, 2026',
    location: 'Hotel ZaZa, Museum District · Houston, TX',
    tag: 'K-12 public sector',
    blurb:
      "The Council of the Great City Schools' finance leadership conference. EZ Cloud automates AP for large public school districts on Oracle E-Business Suite — fund accounting, encumbrances, multi-fund coding, and audit-ready trails built for district controls.",
    detailsUrl: 'https://www.cgcs.org/conferences/upcoming-conferences',
  },
];

const attended: Conference[] = [
  {
    name: 'Oracle AI World Tour — London',
    dates: 'March 2026',
    location: 'ExCeL London, UK',
    tag: 'Oracle ecosystem',
    role: 'Attended',
    detailsUrl: 'https://www.oracle.com/ai-world-tour/',
  },
  {
    name: 'CGCS CFO Conference',
    dates: 'November 2024',
    location: 'Grandover Resort, Greensboro, NC',
    tag: 'K-12 public sector',
    role: 'Attended',
  },
  {
    name: 'Oracle CloudWorld — Las Vegas',
    dates: '2024',
    location: 'The Venetian, Las Vegas, NV',
    tag: 'Oracle ecosystem',
    role: 'Attended',
  },
  {
    name: 'CGCS CFO Conference',
    dates: 'November 2023',
    location: 'Phoenix, AZ',
    tag: 'K-12 public sector',
    role: 'Attended',
  },
  {
    name: 'Oracle CloudWorld — Las Vegas',
    dates: '2023',
    location: 'The Venetian, Las Vegas, NV',
    tag: 'Oracle ecosystem',
    role: 'Exhibited',
  },
  {
    name: 'IOFM AP & P2P Fall Conference',
    dates: 'November 2022',
    location: 'San Antonio, TX',
    tag: 'AP & P2P',
    role: 'Exhibited',
  },
  {
    name: 'Oracle CloudWorld — Las Vegas',
    dates: '2022',
    location: 'The Venetian, Las Vegas, NV',
    tag: 'Oracle ecosystem',
    role: 'Exhibited',
  },
  {
    name: 'APA House of Lords Reception',
    dates: 'December 2022',
    location: 'House of Lords, London, UK',
    tag: 'AP — UK & Europe',
    role: 'Attended',
  },
  {
    name: 'CGCS CFO Conference',
    dates: 'April 2022',
    location: 'St. Petersburg, FL',
    tag: 'K-12 public sector',
    role: 'Attended',
  },
];

const Conferences = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Meet EZ Cloud at Oracle AI World & Industry Conferences | EZ Cloud"
        description="Where to find the EZ Cloud team in 2026 — Oracle AI World (Las Vegas), IOFM AP & P2P (Austin), and the Council of the Great City Schools CFO Conference (Houston). Book a meeting to see Oracle AP automation in person."
        canonical="https://ezcloud.ai/conferences"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Conferences', url: 'https://ezcloud.ai/conferences' },
        ]}
      />
      <Navigation />
      <Breadcrumbs />

      <main className="container mx-auto px-4 py-16 max-w-5xl">
        <header className="mb-12 max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-accent mb-3">
            Meet us in person
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Where to find EZ Cloud
          </h1>
          <p className="text-lg text-gray-600">
            Enterprise AP decisions are made in conversations, not clicks. We exhibit and meet with
            finance and IT leaders at the events where our Oracle and public-sector customers gather.
            Book time with us in advance and we'll walk a real exception from supplier email to Oracle
            posting — against your ERP.
          </p>
        </header>

        <section aria-labelledby="upcoming" className="space-y-6">
          <h2 id="upcoming" className="text-2xl font-semibold text-gray-900">
            Upcoming
          </h2>

          {upcoming.map((c) => (
            <Card key={c.name} className="p-8 bg-white border border-gray-200 shadow-sm">
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="flex-1">
                  <span className="inline-block text-xs font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-3 py-1 mb-3">
                    {c.tag}
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{c.name}</h3>
                  {c.fullName && <p className="text-sm text-gray-500 mb-3">{c.fullName}</p>}

                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-700 mb-4">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-accent" aria-hidden />
                      {c.dates}
                    </span>
                    <span className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-accent" aria-hidden />
                      {c.location}
                    </span>
                  </div>

                  <p className="text-gray-600 leading-relaxed mb-4">{c.blurb}</p>

                  <a
                    href={c.detailsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors"
                  >
                    Event details
                    <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                  </a>
                </div>

                <div className="flex-shrink-0 lg:self-center">
                  <Button variant="accent" onClick={() => openCalendlyDemo()}>
                    Book a meeting
                    <ArrowRight className="ml-2 w-4 h-4" aria-hidden />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </section>

        <section aria-labelledby="attended" className="mt-16 space-y-6">
          <div className="space-y-1">
            <h2 id="attended" className="text-2xl font-semibold text-gray-900">
              Where we've been
            </h2>
            <p className="text-gray-600">
              A track record of showing up — we've exhibited and met finance and IT leaders at Oracle
              and AP industry events across the US and UK.
            </p>
          </div>

          {attended.map((c) => (
            <Card key={`${c.name}-${c.dates}`} className="p-6 bg-gray-50 border border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-green-700 bg-green-100 rounded-full px-3 py-1 self-start">
                  <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
                  {c.role || 'Attended'}
                </span>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900">{c.name}</h3>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600 mt-1">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-gray-400" aria-hidden />
                      {c.dates}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-gray-400" aria-hidden />
                      {c.location}
                    </span>
                  </div>
                  {c.blurb && <p className="text-sm text-gray-600 mt-2">{c.blurb}</p>}
                </div>
                {c.detailsUrl && (
                  <a
                    href={c.detailsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors flex-shrink-0"
                  >
                    Event details
                    <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                  </a>
                )}
              </div>
            </Card>
          ))}
        </section>

        <section className="mt-16 text-center border-t border-gray-200 pt-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">
            Not attending? Let's still talk.
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            We run live walkthroughs year-round. Book a 30-minute session and we'll show AP automation
            against Oracle Fusion, EBS, or your ERP — no commitment.
          </p>
          <Button size="lg" variant="accent" onClick={() => openCalendlyDemo()}>
            Book a demo
            <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
          </Button>
        </section>
      </main>
    </div>
  );
};

export default Conferences;
